import { KnownId } from "@fairfleet/geotab";
import { useQuery } from "@tanstack/react-query";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { readingMs } from "@/pages/live-data/utilities/newestReading";

/** Before any unit was ever installed; Geotab returns a search from there oldest first. */
const BEGINNING = new Date(0);

/** Readings that can come first without the unit having sent them; the odometer set at installation. */
const ENTERED_BY_HAND = new Set<string>([KnownId.DiagnosticOdometerAdjustmentId]);

/**
 * When the vehicle's unit first sent anything: its first GPS point or first reading, whichever
 * came first. Says when the unit was really installed and running, where "active from" in
 * MyGeotab only says when the vehicle was set up in the database.
 *
 * Two calls, bundled into one by the client. Readings entered by hand are skipped: on 669
 * regiobus the odometer set at installation comes 16 minutes before the unit's first GPS point.
 */
export function useFirstRecordAt(deviceId: string | undefined) {
  const { geotabClient, activeSessionId } = useGeotabClient();

  return useQuery({
    queryKey: ["firstRecordAt", activeSessionId, deviceId],
    queryFn: async ({ signal }) => {
      const [logRecords, statusData] = await Promise.all([
        geotabClient.get(
          "LogRecord",
          { deviceSearch: { id: deviceId }, fromDate: BEGINNING },
          1,
          signal,
        ),
        geotabClient.get(
          "StatusData",
          { deviceSearch: { id: deviceId }, fromDate: BEGINNING },
          5,
          signal,
        ),
      ]);
      const firstReading = statusData.find(
        reading => !ENTERED_BY_HAND.has(reading.diagnostic?.id ?? ""),
      );
      const candidates = [logRecords[0]?.dateTime, firstReading?.dateTime]
        .filter((at): at is Date => !!at)
        .map(readingMs);
      return candidates.length > 0 ? new Date(Math.min(...candidates)) : null;
    },
    enabled: !!deviceId,
    // The first record never changes once there is one.
    staleTime: Infinity,
  });
}
